import {
  AppstoreAddOutlined,
  DollarCircleOutlined,
  PieChartOutlined,
  ShoppingCartOutlined,
  TagsOutlined,
} from '@ant-design/icons'
import { Card, List, Spin } from 'antd'
import { useMemo } from 'react'

import { SellingStatusEnum } from 'constants/pto-v2'
import { usePTOV2Context } from 'context/pto-v2-context-provider'
import { useCollectionTotalSupply } from 'hooks/pto-v2/use-collection-total-supply'
import { usePoolInfo } from 'hooks/pto-v2/use-pool-info'
import { formatNumber, safeDivide } from 'utils/formatted-number'

/**
 * PTO v2 statistics. Price per NFT and Funding Target show "--" while the
 * PTO is Off Sale (Confluence 2.3). Pool numbers come from PTODistribution.
 */
const Overview = () => {
  const { loading, ptoDetail, contractAddress } = usePTOV2Context()

  const { poolInfo } = usePoolInfo(contractAddress)
  const { totalSupply: totalMinted } = useCollectionTotalSupply(contractAddress)

  const detail = useMemo(() => {
    const sellingStatus: SellingStatusEnum = ptoDetail?.sellingStatus ?? SellingStatusEnum.OFF_SALE
    const isOffSale = sellingStatus === SellingStatusEnum.OFF_SALE

    const maxNFTSupply = Number(ptoDetail?.maxNFTSupply || 0)
    const pricePerNFT = Number(ptoDetail?.pricePerNFT || 0)
    const fundingTarget = maxNFTSupply * pricePerNFT
    const totalNFTsPurchase = poolInfo?.totalNFTsPurchase || 0
    const percent = safeDivide(totalNFTsPurchase, maxNFTSupply) * 100

    return [
      {
        title: 'Funding target (USDC)',
        value: isOffSale ? '--' : formatNumber(fundingTarget, 3),
        icon: (
          <DollarCircleOutlined
            className="icons-style"
            style={{ background: 'lightgreen', color: 'green' }}
          />
        ),
      },
      {
        title: 'Total supply',
        value: maxNFTSupply,
        icon: (
          <AppstoreAddOutlined
            className="icons-style"
            style={{ background: 'lightblue', color: 'blue' }}
          />
        ),
      },
      {
        title: 'Total NFTs Purchased',
        value: totalNFTsPurchase,
        icon: (
          <ShoppingCartOutlined
            className="icons-style"
            style={{ background: 'lightcoral', color: 'red' }}
          />
        ),
      },
      {
        title: 'Total Minted',
        value: totalMinted,
        icon: (
          <TagsOutlined
            className="icons-style"
            style={{ background: 'lightyellow', color: 'goldenrod' }}
          />
        ),
      },
      {
        title: 'Price per NFT (USDC)',
        value: isOffSale ? '--' : formatNumber(pricePerNFT, 3),
        icon: (
          <DollarCircleOutlined
            className="icons-style"
            style={{ background: 'lightgreen', color: 'darkgreen' }}
          />
        ),
      },
      {
        value: (
          <>
            Sold&nbsp;
            <span>{percent.toFixed(2)}%</span>
          </>
        ),
        icon: (
          <PieChartOutlined
            className="icons-style"
            style={
              totalNFTsPurchase > 0 && totalNFTsPurchase === maxNFTSupply
                ? { background: 'lightgreen', color: 'darkgreen' }
                : { background: 'lightyellow', color: 'orange' }
            }
          />
        ),
      },
    ]
  }, [ptoDetail, poolInfo, totalMinted])

  return (
    <List
      grid={{ gutter: 16, column: 4 }}
      dataSource={detail}
      className="detail-card"
      renderItem={(item, index) => (
        <>
          <List.Item key={index}>
            {
              <Card className={'card-style'}>
                <div className={'content-style'}>
                  <div>{item.icon}</div>
                  {!loading ? (
                    <div>
                      <div className={'title-style'}>{item.title}</div>
                      <div className={index === 5 ? 'value-style last' : 'value-style'}>
                        {item.value}
                      </div>
                    </div>
                  ) : (
                    <Spin />
                  )}
                </div>
              </Card>
            }
          </List.Item>
        </>
      )}
    />
  )
}

export default Overview
